import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

export type EmailProvider =
  | 'SMTP'
  | 'SENDGRID'
  | 'MAILCHIMP'
  | 'SMTP_OAUTH2_MICROSOFT'
  | 'MICROSOFT_GRAPH';

export interface ProviderResponse {
  active_provider: EmailProvider;
}

export interface SMTPConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  from_email: string;
  use_tls?: boolean;
  from_name?: string;
}

export interface MailchimpConfig {
  api_key: string;
  from_email: string;
  from_name?: string;
}

export interface SendGridConfig {
  api_key: string;
  from_email: string;
  from_name?: string;
}

export interface MicrosoftOAuth2Config {
  client_id: string;
  client_secret: string;
  tenant_id: string;
  from_email: string;
  from_name?: string;
  redirect_uri?: string;
}

export interface OAuth2StatusResponse {
  connected: boolean;
}

export interface AuthUrlResponse {
  auth_url: string;
}

export interface TestEmailRequest {
  to: string;
}

export interface TestEmailResponse {
  detail: string;
}

export const emailConfigApi = createApi({
  reducerPath: 'emailConfigApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: [
    'EmailProvider',
    'SMTPConfig',
    'MailchimpConfig',
    'SendGridConfig',
    'SmtpOAuth2MicrosoftConfig',
    'SmtpOAuth2MicrosoftStatus',
    'MicrosoftGraphConfig',
  ],
  endpoints: (builder) => ({
    getEmailProvider: builder.query<ProviderResponse, void>({
      query: () => 'email-config/provider/',
      providesTags: ['EmailProvider'],
    }),
    setEmailProvider: builder.mutation<ProviderResponse, { provider: EmailProvider }>({
      query: (data) => ({
        url: 'email-config/provider/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['EmailProvider'],
    }),
    getSmtpConfig: builder.query<SMTPConfig, void>({
      query: () => 'email-config/smtp/',
      providesTags: ['SMTPConfig'],
    }),
    updateSmtpConfig: builder.mutation<SMTPConfig, Partial<SMTPConfig>>({
      query: (data) => ({
        url: 'email-config/smtp/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SMTPConfig'],
    }),
    getMailchimpConfig: builder.query<MailchimpConfig, void>({
      query: () => 'email-config/mailchimp/',
      providesTags: ['MailchimpConfig'],
    }),
    updateMailchimpConfig: builder.mutation<MailchimpConfig, Partial<MailchimpConfig>>({
      query: (data) => ({
        url: 'email-config/mailchimp/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['MailchimpConfig'],
    }),
    getSendgridConfig: builder.query<SendGridConfig, void>({
      query: () => 'email-config/sendgrid/',
      providesTags: ['SendGridConfig'],
    }),
    updateSendgridConfig: builder.mutation<SendGridConfig, Partial<SendGridConfig>>({
      query: (data) => ({
        url: 'email-config/sendgrid/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SendGridConfig'],
    }),
    testEmailConfig: builder.mutation<TestEmailResponse, TestEmailRequest>({
      query: (data) => ({
        url: 'email-config/test/',
        method: 'POST',
        body: data,
      }),
    }),

    // --- SMTP OAuth2 Microsoft ---
    getSmtpOAuth2MicrosoftConfig: builder.query<MicrosoftOAuth2Config, void>({
      query: () => 'email-config/smtp-oauth2-microsoft/',
      providesTags: ['SmtpOAuth2MicrosoftConfig'],
    }),
    updateSmtpOAuth2MicrosoftConfig: builder.mutation<MicrosoftOAuth2Config, Partial<MicrosoftOAuth2Config>>({
      query: (data) => ({
        url: 'email-config/smtp-oauth2-microsoft/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SmtpOAuth2MicrosoftConfig'],
    }),
    getSmtpOAuth2MicrosoftStatus: builder.query<OAuth2StatusResponse, void>({
      query: () => 'email-config/smtp-oauth2-microsoft/status/',
      providesTags: ['SmtpOAuth2MicrosoftStatus'],
    }),
    getSmtpOAuth2MicrosoftAuthUrl: builder.query<AuthUrlResponse, void>({
      query: () => 'email-config/smtp-oauth2-microsoft/auth-url/',
    }),

    // --- Microsoft Graph API ---
    getMicrosoftGraphConfig: builder.query<MicrosoftOAuth2Config, void>({
      query: () => 'email-config/microsoft-graph/',
      providesTags: ['MicrosoftGraphConfig'],
    }),
    updateMicrosoftGraphConfig: builder.mutation<MicrosoftOAuth2Config, Partial<MicrosoftOAuth2Config>>({
      query: (data) => ({
        url: 'email-config/microsoft-graph/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['MicrosoftGraphConfig'],
    }),
  }),
});

export const {
  useGetEmailProviderQuery,
  useSetEmailProviderMutation,
  useGetSmtpConfigQuery,
  useUpdateSmtpConfigMutation,
  useGetMailchimpConfigQuery,
  useUpdateMailchimpConfigMutation,
  useGetSendgridConfigQuery,
  useUpdateSendgridConfigMutation,
  useTestEmailConfigMutation,
  useGetSmtpOAuth2MicrosoftConfigQuery,
  useUpdateSmtpOAuth2MicrosoftConfigMutation,
  useGetSmtpOAuth2MicrosoftStatusQuery,
  useLazyGetSmtpOAuth2MicrosoftAuthUrlQuery,
  useGetMicrosoftGraphConfigQuery,
  useUpdateMicrosoftGraphConfigMutation,
} = emailConfigApi;
